import { Person } from '@store/models/Person';
import { isDefined } from '@utils/isDefined';
import { toArrayFromIterable } from '@utils/toArrayFromIterable';
import type { Nullable, Optional } from 'chaika-common-types';
import { Languages as UserLanguages, Locale, RoleName, UserStatus } from 'chaika-enums';

import type {
    RedirectPhoneNumberData,
    RoleData,
    UserData,
    UserProfileData,
    UserUpdateForm,
} from 'chaika-frontend-types/frontend-cms';
import { PracticeContactViewModel, UserTranslationModel } from 'chaika-types';
import { action, computed, makeObservable, observable } from 'mobx';

export class UserProfile extends Person {
    @observable public readonly id: string;
    @observable public readonly isTechnical: boolean;
    @observable public status: UserStatus;
    @observable public email: string;
    @observable public roles: RoleData[];
    @observable public canBeEpisodeManager: boolean;
    @observable public shortPhoneNumber?: Nullable<number>;
    @observable public phone?: string;
    @observable public contactEmail?: string;
    @observable public workplace?: string;
    @observable public cityPhone?: string;
    @observable public patientWorkplaceGroupId?: string;
    @observable public startOfCareer?: Nullable<string>;
    @observable public degrees?: string;
    @observable public introductory?: string;
    @observable public education?: string;
    @observable public experience?: string;
    @observable public publications?: string;
    @observable public reasonForWork?: string;
    @observable public taxNumber?: string;
    @observable public snils?: Nullable<string>;
    @observable public nsiPositionCode?: Nullable<string>;
    @observable public frmrPositionCode?: Nullable<string>;
    @observable public remdRoleCode?: Nullable<string>;
    @observable public mainOrganizationId?: Nullable<string>;
    @observable public mainSpecialtyId?: Nullable<string>;
    @observable public languages?: UserLanguages[];
    @observable public redirectPhoneNumbers: RedirectPhoneNumberData[] = [];
    @observable public selectedUsers = new Set<string>();
    @observable public translations = new Map<Locale, UserTranslationModel>();
    @observable private practices: Optional<PracticeContactViewModel[]>;
    @observable private profileDataSet = false;

    constructor(
        {
            id,
            email,
            status,
            roles = [],
            canBeEpisodeManager = false,
            phone,
            startOfCareer,
            degrees,
            introductory,
            whatsApp,
            profileUrl,
            education,
            experience,
            publications,
            reasonForWork,
            contactEmail,
            languages,
            workplace,
            cityPhone,
            patientWorkplaceGroupId,
            mainOrganizationId,
            mainSpecialtyId,
            avatar,
            avatarUnits,
            shortPhoneNumber,
            redirectPhoneNumbers = [],
            selectedUserIds,
            translations = [],
            isTechnical = false,
            taxNumber,
            snils,
            nsiPositionCode,
            frmrPositionCode,
            remdRoleCode,
            ...person
        }: UserData = {} as UserData,
    ) {
        super(person);
        makeObservable(this);

        this.id = id;
        this.email = email;
        this.status = status;
        this.roles = roles;
        this.canBeEpisodeManager = canBeEpisodeManager;
        this.profileDataSet = true;
        this.phone = phone;
        this.whatsApp = whatsApp;
        this.contactEmail = contactEmail;
        this.startOfCareer = startOfCareer;
        this.degrees = degrees;
        this.introductory = introductory;
        this.profileUrl = profileUrl;
        this.education = education;
        this.experience = experience;
        this.publications = publications;
        this.reasonForWork = reasonForWork;
        this.workplace = workplace;
        this.cityPhone = cityPhone;
        this.patientWorkplaceGroupId = patientWorkplaceGroupId;
        this.mainOrganizationId = mainOrganizationId;
        this.mainSpecialtyId = mainSpecialtyId;
        this.languages = languages;
        this.avatar = avatar;
        this.avatarUnits = avatarUnits;
        this.shortPhoneNumber = shortPhoneNumber;
        this.redirectPhoneNumbers = redirectPhoneNumbers;
        this.isTechnical = isTechnical;
        this.taxNumber = taxNumber;
        this.snils = snils;
        this.nsiPositionCode = nsiPositionCode;
        this.frmrPositionCode = frmrPositionCode;
        this.remdRoleCode = remdRoleCode;

        this.addTranslations(translations);
        this.addSelectedUsers(selectedUserIds ?? []);
    }

    @computed
    public get login(): string {
        const firtsPathOfEmail = this.email.match(/.*?@/gi)?.[0] ?? this.email;
        return firtsPathOfEmail.replace('@', '');
    }

    @computed
    public get workplaceProfileUrl(): Optional<string> {
        return !!this.workplace ? `https://astra.workplace.com/profile.php?id=${this.workplace}` : undefined;
    }

    @computed
    public get hasShortNumber(): boolean {
        return !!this.shortPhoneNumber;
    }

    @computed
    public get isArchived(): boolean {
        return this.status === UserStatus.Archived;
    }

    @computed
    public get isProfileDataSet(): boolean {
        return this.profileDataSet;
    }

    @computed
    public get isAssistant(): boolean {
        return (this.roles || []).some(({ name }) => name === 'assistant');
    }

    @computed
    public get isAssistantStrict(): boolean {
        return this.isAssistant && !this.isDoctor;
    }

    @computed
    public get isAccountant(): boolean {
        return (this.roles || []).some(({ name }) => name === 'accountant');
    }

    @computed
    public get isOnlyAccountant(): boolean {
        return this.isAccountant && this.roles.length === 1;
    }

    @computed
    public get isDoctor(): boolean {
        return (this.roles || []).some(({ name }) => name === RoleName.Doctor);
    }

    @computed
    public get isProcedureNurse(): boolean {
        return (this.roles || []).some(({ name }) => name === RoleName.ProcedureNurse);
    }

    @computed
    public get isMedicalDirector(): boolean {
        return (this.roles || []).some(({ name }) => name === RoleName.MedicalDirector);
    }

    @computed
    public get isCare(): boolean {
        return (this.roles || []).some(({ name }) => name === RoleName.Care);
    }

    @computed
    public get hasLaboratoryDiagnosticsAccess(): boolean {
        return this.hasRootAccess || this.isProcedureNurse || this.isMedicalDirector || this.isCare;
    }

    @computed
    public get isDirector(): boolean {
        return (
            this.hasRootAccess ||
            (this.roles.length > 0 &&
                this.roles.some(({ name }) => [RoleName.PracticeLeader, RoleName.HeadOfClinic].includes(name)))
        );
    }

    @computed
    public get isHeadOfClinic(): boolean {
        return (this.roles || []).some(({ name }) => name === RoleName.HeadOfClinic);
    }

    @computed
    public get isPracticeLeader(): boolean {
        return (this.roles || []).some(({ name }) => name === RoleName.PracticeLeader);
    }

    @computed
    public get isDeveloper(): boolean {
        return (this.roles || []).some(({ name }) => [RoleName.Developer].includes(name));
    }

    @computed
    public get hasRootAccess(): boolean {
        return (this.roles || []).some(({ name }) => [RoleName.Helpdesk, RoleName.Developer].includes(name));
    }

    @computed
    public get isHelpdesk(): boolean {
        return (this.roles || []).some(({ name }) => [RoleName.Helpdesk].includes(name));
    }

    @computed
    public get selectedUserIds(): string[] {
        return [...this.selectedUsers.values()];
    }

    public get allPractices(): PracticeContactViewModel[] {
        return this.practices ?? [];
    }

    public get practiceWithTgLint(): PracticeContactViewModel[] {
        return this.practices?.filter(({ telegramLink }) => !!telegramLink && telegramLink.length > 0) ?? [];
    }

    public get isPracticesSet(): boolean {
        return isDefined(this.practices);
    }

    @action
    public setTranslations(fieldName: string, value: string, locale: Locale): void {
        const translation = this.translations.get(locale) ?? ({} as UserTranslationModel);

        this.translations.set(locale, { ...translation, locale, [fieldName]: value });
    }

    @action
    public setPractices(practices: PracticeContactViewModel[]): void {
        this.practices = practices;
    }

    @action
    public setProfileData({
        telegram = this.telegram,
        telegramName = this.telegramName,
        phone = this.phone,
        skype = this.skype,
        email = this.email,
        startOfCareer = this.startOfCareer,
        degrees = this.degrees,
        introductory = this.introductory,
        education = this.education,
        experience = this.experience,
        contactEmail = this.contactEmail,
        whatsApp = this.whatsApp,
        publications = this.publications,
        reasonForWork = this.reasonForWork,
        workplace = this.workplace,
        cityPhone = this.cityPhone,
        patientWorkplaceGroupId = this.patientWorkplaceGroupId,
        mainOrganizationId = this.mainOrganizationId,
        mainSpecialtyId = this.mainSpecialtyId,
        shortPhoneNumber = this.shortPhoneNumber,
        redirectPhoneNumbers = this.redirectPhoneNumbers,
        languages = this.languages,
        avatar = this.avatar,
        avatarUnits = this.avatarUnits,
        canBeEpisodeManager = this.canBeEpisodeManager,
        taxNumber = this.taxNumber,
        snils = this.snils,
        nsiPositionCode = this.nsiPositionCode,
        frmrPositionCode = this.frmrPositionCode,
        remdRoleCode = this.remdRoleCode,
        translations = [],
    }: Partial<Omit<UserProfileData, 'id'> & { redirectPhoneNumbers?: RedirectPhoneNumberData[] }>): void {
        this.profileDataSet = true;
        this.phone = phone;
        this.whatsApp = whatsApp;
        this.telegram = telegram;
        this.telegramName = telegramName;
        this.skype = skype;
        this.email = email;
        this.contactEmail = contactEmail;
        this.startOfCareer = startOfCareer;
        this.degrees = degrees;
        this.introductory = introductory;
        this.education = education;
        this.experience = experience;
        this.publications = publications;
        this.reasonForWork = reasonForWork;
        this.shortPhoneNumber = shortPhoneNumber;
        this.workplace = workplace;
        this.cityPhone = cityPhone;
        this.patientWorkplaceGroupId = patientWorkplaceGroupId;
        this.mainOrganizationId = mainOrganizationId;
        this.mainSpecialtyId = mainSpecialtyId;
        this.languages = languages;
        this.avatar = avatar;
        this.redirectPhoneNumbers = redirectPhoneNumbers;
        this.avatarUnits = avatarUnits;
        this.canBeEpisodeManager = canBeEpisodeManager;
        this.taxNumber = taxNumber;
        this.snils = snils;
        this.nsiPositionCode = nsiPositionCode;
        this.frmrPositionCode = frmrPositionCode;
        this.remdRoleCode = remdRoleCode;
        this.addTranslations(translations);
    }

    @action
    public updateStatus(status: UserStatus): void {
        this.status = status;
    }

    @action
    public update({
        firstName = this.firstName,
        lastName = this.lastName,
        patronymic = this.patronymic,
        accountFirstName = this.accountFirstName,
        accountLastName = this.accountLastName,
        accountPatronymic = this.accountPatronymic,
        oldLastName = this.oldLastName,
        avatar = this.avatar,
        engFirstName = this.engFirstName,
        engLastName = this.engLastName,
        engOldLastName = this.engOldLastName,
        engPatronymic = this.engPatronymic,
        profileUrl = this.profileUrl,
        taxNumber = this.taxNumber,
        snils = this.snils,
        nsiPositionCode = this.nsiPositionCode,
        frmrPositionCode = this.frmrPositionCode,
        remdRoleCode = this.remdRoleCode,
        ...profileData
    }: UserUpdateForm): void {
        this.firstName = firstName;
        this.lastName = lastName;
        this.oldLastName = oldLastName;
        this.patronymic = patronymic;
        this.avatar = avatar;
        this.engFirstName = engFirstName;
        this.engLastName = engLastName;
        this.engOldLastName = engOldLastName;
        this.engPatronymic = engPatronymic;
        this.profileUrl = profileUrl;
        this.accountFirstName = accountFirstName;
        this.accountLastName = accountLastName;
        this.accountPatronymic = accountPatronymic;
        this.taxNumber = taxNumber;
        this.snils = snils;
        this.nsiPositionCode = nsiPositionCode;
        this.frmrPositionCode = frmrPositionCode;
        this.remdRoleCode = remdRoleCode;
        // @ts-ignore
        this.setProfileData(profileData);
    }

    @action
    public addSelectedUsers(userIds: string[]): void {
        userIds.forEach(id => this.selectedUsers.add(id));
    }

    @action
    public addTranslations(translations: UserTranslationModel[]): void {
        translations.forEach(translation => this.translations.set(translation.locale, translation));
    }

    @action
    public removeSelectedUser(userId: string): void {
        this.selectedUsers.delete(userId);
    }

    public addRole(role: RoleData) {
        this.roles.push(role);
    }

    public removeRole(roleId: string) {
        this.roles = this.roles.filter(role => role.id !== roleId);
    }

    public serialize(): UserProfileData {
        return {
            id: this.id,
            status: this.status,
            firstName: this.firstName,
            lastName: this.lastName,
            oldLastName: this.oldLastName,
            patronymic: this.patronymic,
            engFirstName: this.engFirstName,
            engLastName: this.engLastName,
            engOldLastName: this.engOldLastName,
            engPatronymic: this.engPatronymic,
            accountFirstName: this.accountFirstName,
            accountLastName: this.accountLastName,
            accountPatronymic: this.accountPatronymic,
            avatar: this.avatar,
            avatarUnits: this.avatarUnits,
            profileUrl: this.profileUrl,
            email: this.email,
            phone: this.phone,
            contactEmail: this.contactEmail,
            whatsApp: this.whatsApp,
            telegram: this.telegram,
            telegramName: this.telegramName,
            skype: this.skype,
            startOfCareer: this.startOfCareer,
            degrees: this.degrees,
            introductory: this.introductory,
            education: this.education,
            experience: this.experience,
            publications: this.publications,
            reasonForWork: this.reasonForWork,
            workplace: this.workplace,
            cityPhone: this.cityPhone,
            patientWorkplaceGroupId: this.patientWorkplaceGroupId,
            mainOrganizationId: this.mainOrganizationId,
            mainSpecialtyId: this.mainSpecialtyId,
            languages: this.languages,
            canBeEpisodeManager: this.canBeEpisodeManager,
            taxNumber: this.taxNumber,
            snils: this.snils || null,
            nsiPositionCode: this.nsiPositionCode || null,
            frmrPositionCode: this.frmrPositionCode || null,
            remdRoleCode: this.remdRoleCode || null,
            translations: toArrayFromIterable(this.translations),
            selectedUserIds: this.selectedUserIds,
        };
    }
}
